import { useState, useEffect, useCallback } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ClipboardList, Plus, Trash2, Save, FileDown, Sparkles, Upload, ChevronDown, Pencil, GripVertical,
  Languages, FileText, RefreshCw, Check, X, ArrowUp, ArrowDown, Clock, User, Eye
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const LANG_OPTIONS = [
  { id: "de", label: "Deutsch", flag: "DE" },
  { id: "pt", label: "Português", flag: "PT" },
  { id: "en", label: "English", flag: "EN" },
];

const TYPE_LABELS = { text: "Kurzantwort", textarea: "Freitext", choice: "Auswahl" };

const CAT_COLORS = {
  personal: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", header: "bg-blue-600" },
  health: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", header: "bg-rose-600" },
  eating: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", header: "bg-amber-600" },
  preferences: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", header: "bg-emerald-600" },
  lifestyle: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", header: "bg-violet-600" },
  goals: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", header: "bg-sky-600" },
};

export default function AnamnesePage({ onLogout }) {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [pdfLang, setPdfLang] = useState("de");
  const [showAddQ, setShowAddQ] = useState(false);
  const [editingQ, setEditingQ] = useState(null);
  const [newQ, setNewQ] = useState({ de: "", pt: "", en: "", category: "goals", type: "textarea", options_de: "", options_pt: "", options_en: "" });
  const [generating, setGenerating] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [collapsedCats, setCollapsedCats] = useState({});
  const [displayLang, setDisplayLang] = useState("de");
  const [downloading, setDownloading] = useState(false);
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeTab, setActiveTab] = useState("editor"); // "editor" | "records"

  const loadQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/anamnese/questions`, { headers: getHeaders() });
      setQuestions(res.data.questions || []);
      setCategories(res.data.categories || {});
    } catch { toast.error("Fehler beim Laden"); }
    setLoading(false);
  }, []);

  const loadRecords = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/anamnese/records`, { headers: getHeaders() });
      setRecords(res.data.records || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadQuestions(); loadRecords(); }, [loadQuestions, loadRecords]);

  const grouped = questions.reduce((acc, q) => {
    const cat = q.category || "goals";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(q);
    return acc;
  }, {});

  const handleAddQuestion = async () => {
    if (!newQ.de.trim()) { toast.error("Frage (DE) erforderlich"); return; }
    try {
      await axios.post(`${API}/anamnese/questions`, newQ, { headers: getHeaders() });
      toast.success("Frage hinzugefügt");
      setShowAddQ(false);
      setNewQ({ de: "", pt: "", en: "", category: "goals", type: "textarea", options_de: "", options_pt: "", options_en: "" });
      loadQuestions();
    } catch { toast.error("Fehler"); }
  };

  const handleUpdateQuestion = async () => {
    if (!editingQ) return;
    try {
      await axios.put(`${API}/anamnese/questions/${editingQ.id}`, editingQ, { headers: getHeaders() });
      toast.success("Aktualisiert");
      setEditingQ(null);
      loadQuestions();
    } catch { toast.error("Fehler"); }
  };

  const handleDeleteQuestion = async (qid) => {
    try {
      await axios.delete(`${API}/anamnese/questions/${qid}`, { headers: getHeaders() });
      toast.success("Gelöscht");
      loadQuestions();
    } catch { toast.error("Fehler"); }
  };

  const handleMoveQuestion = async (qid, direction) => {
    const idx = questions.findIndex(q => q.id === qid);
    if (idx < 0) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= questions.length) return;
    const reordered = [...questions];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setQuestions(reordered);
    try {
      await axios.post(`${API}/anamnese/questions/reorder`, { order: reordered.map(q => q.id) }, { headers: getHeaders() });
    } catch { toast.error("Fehler bei Neuordnung"); }
  };

  const handleAIGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/anamnese/questions/ai-generate`, { context: aiContext }, { headers: getHeaders(), timeout: 60000 });
      toast.success(`${res.data.count} Fragen generiert`);
      setShowAiDialog(false);
      setAiContext("");
      loadQuestions();
    } catch (err) { toast.error(err?.response?.data?.detail || "KI-Generierung fehlgeschlagen"); }
    setGenerating(false);
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/anamnese/pdf?lang=${pdfLang}`, { headers: getHeaders(), responseType: "blob", timeout: 30000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Anamnese_${pdfLang.toUpperCase()}.pdf`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch { toast.error("PDF-Export fehlgeschlagen"); }
    setDownloading(false);
  };

  const handleUploadPDF = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/anamnese/upload-pdf`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setUploadResult(res.data);
      toast.success("PDF analysiert — Antworten extrahiert und auf Deutsch übersetzt");
      loadRecords();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleDeleteRecord = async (recordId) => {
    try {
      await axios.delete(`${API}/anamnese/records/${recordId}`, { headers: getHeaders() });
      toast.success("Anamnese gelöscht");
      if (selectedRecord?.id === recordId) setSelectedRecord(null);
      loadRecords();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const handleLoadRecord = (record) => {
    setSelectedRecord(record);
    setUploadResult({ record_id: record.id, answers: record.answers });
    setActiveTab("editor");
    toast.success(`Anamnese vom ${new Date(record.created_at).toLocaleDateString("de-DE")} geladen`);
  };


  const handleGeneratePlan = async () => {
    if (!uploadResult?.answers) { toast.error("Bitte zuerst ein ausgefülltes PDF hochladen"); return; }
    setGeneratingPlan(true);
    try {
      const res = await axios.post(`${API}/anamnese/generate-plan`, { answers: uploadResult.answers }, { headers: getHeaders(), timeout: 120000 });
      // Save as nutrition plan with Energiebilanz data from anamnese
      const planPayload = {
        name: res.data.plan_name || "Anamnese-Plan",
        meals: res.data.meals,
        energiebilanz: res.data.energiebilanz || {},
      };
      const saved = await axios.post(`${API}/nutrition-plans`, planPayload, { headers: getHeaders() });
      const summaryMsg = res.data.summary ? `\n\n${res.data.summary}` : "";
      toast.success(`Ernährungsplan erstellt! ${res.data.client_name ? `Für: ${res.data.client_name}` : ""}${summaryMsg ? " — Öffne den Ernährungsplan-Bereich." : ""}`, { duration: 8000 });
      setUploadResult(prev => ({ ...prev, plan: res.data, savedPlanId: saved.data.id }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Plan-Generierung fehlgeschlagen"); }
    setGeneratingPlan(false);
  };

  if (loading) return <Layout onLogout={onLogout}><div className="flex items-center justify-center h-full"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;

  return (
    <Layout onLogout={onLogout}>
      <div className="flex h-full" style={{ height: "calc(100vh - 64px)" }}>
        {/* Sidebar */}
        <div className="w-72 flex-shrink-0 border-r border-slate-200 p-4 flex flex-col gap-4 bg-white">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-emerald-500" />
            <h1 className="text-lg font-bold text-slate-900" data-testid="anamnese-title">Anamnese</h1>
          </div>

          {/* PDF Download */}
          <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="pdf-download-section">
            <p className="text-[10px] font-bold text-slate-500 uppercase">PDF herunterladen (leer)</p>
            <div className="flex gap-1">
              {LANG_OPTIONS.map(l => (
                <button key={l.id} onClick={() => setPdfLang(l.id)}
                  className={`flex-1 py-1.5 rounded text-[10px] font-bold ${pdfLang === l.id ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}
                  data-testid={`pdf-lang-${l.id}`}>{l.flag}</button>
              ))}
            </div>
            <Button size="sm" className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleDownloadPDF} disabled={downloading} data-testid="download-pdf-btn">
              {downloading ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
              PDF {pdfLang.toUpperCase()} herunterladen
            </Button>
          </div>

          {/* PDF Upload */}
          <div className="border border-emerald-200 rounded-lg p-3 space-y-2 bg-emerald-50/30" data-testid="pdf-upload-section">
            <p className="text-[10px] font-bold text-emerald-600 uppercase">Ausgefülltes PDF hochladen</p>
            <p className="text-[9px] text-slate-400">KI liest handschriftliche & digitale Antworten</p>
            <label className="flex items-center justify-center gap-1.5 py-2 border-2 border-dashed border-emerald-300 rounded-lg cursor-pointer hover:bg-emerald-50 transition-colors">
              {uploading ? <RefreshCw className="w-4 h-4 text-emerald-500 animate-spin" /> : <Upload className="w-4 h-4 text-emerald-500" />}
              <span className="text-xs font-semibold text-emerald-600">{uploading ? "Analysiere..." : "PDF auswählen"}</span>
              <input type="file" accept=".pdf" className="hidden" onChange={handleUploadPDF} disabled={uploading} data-testid="upload-pdf-input" />
            </label>
            {uploadResult && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs text-emerald-700">
                  <Check className="w-3.5 h-3.5" />
                  <span className="font-semibold">{Object.keys(uploadResult.answers || {}).filter(k => uploadResult.answers[k]).length} Antworten erkannt</span>
                </div>
                <Button size="sm" className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white" onClick={handleGeneratePlan} disabled={generatingPlan} data-testid="generate-plan-btn">
                  {generatingPlan ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generiere Plan...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Ernährungsplan erstellen</>}
                </Button>
                {uploadResult.plan && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-emerald-600 font-medium">{uploadResult.plan.plan_name}</p>
                    {uploadResult.plan.summary && (
                      <p className="text-[9px] text-slate-500 leading-relaxed">{uploadResult.plan.summary}</p>
                    )}
                    {uploadResult.plan.energiebilanz?.gewicht && (
                      <p className="text-[9px] text-slate-400">Gewicht: {uploadResult.plan.energiebilanz.gewicht} kg | Ziel: {uploadResult.plan.energiebilanz.goal}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="mt-auto space-y-2">
            {records.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-3" data-testid="records-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Gespeicherte Anamnesen</p>
                  <span className="text-[10px] font-bold text-slate-400">{records.length}</span>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {records.map(r => {
                    const name = r.answers?.["q-name"] || "Unbekannt";
                    const date = new Date(r.created_at).toLocaleDateString("de-DE");
                    const filled = Object.values(r.answers || {}).filter(v => v).length;
                    const isActive = selectedRecord?.id === r.id;
                    return (
                      <div key={r.id} className={`flex items-center gap-2 p-1.5 rounded-lg cursor-pointer group ${isActive ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50'}`}
                        data-testid={`record-${r.id}`}>
                        <div className="flex-1 min-w-0" onClick={() => handleLoadRecord(r)}>
                          <p className="text-[11px] font-semibold text-slate-700 truncate">{name}</p>
                          <p className="text-[9px] text-slate-400">{date} • {filled} Antworten</p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteRecord(r.id); }}
                          className="p-0.5 rounded opacity-0 group-hover:opacity-100 text-red-400 hover:bg-red-50">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="text-[10px] text-slate-400">
              <p>{questions.length} Fragen • {Object.keys(grouped).length} Kategorien</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {/* Header bar */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-bold text-slate-800">Fragebogen-Editor</h2>
              <div className="flex gap-1">
                {LANG_OPTIONS.map(l => (
                  <button key={l.id} onClick={() => setDisplayLang(l.id)}
                    className={`px-2 py-0.5 rounded text-[10px] font-bold ${displayLang === l.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'}`}
                    data-testid={`display-lang-${l.id}`}>{l.flag}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowAiDialog(true)} data-testid="ai-generate-questions-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> KI Fragen generieren
              </Button>
              <Button size="sm" onClick={() => setShowAddQ(true)} data-testid="add-question-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Frage hinzufügen
              </Button>
            </div>
          </div>

          {/* Upload Results Preview */}
          {uploadResult?.answers && (
            <div className="bg-white rounded-xl border border-emerald-200 p-4 mb-5" data-testid="upload-results">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-emerald-700 flex items-center gap-1.5">
                  <FileText className="w-4 h-4" /> Extrahierte Antworten
                </h3>
                <button onClick={() => setUploadResult(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {Object.entries(uploadResult.answers).filter(([, v]) => v).map(([qid, answer]) => {
                  const q = questions.find(qq => qq.id === qid);
                  return (
                    <div key={qid} className="text-xs border border-slate-100 rounded p-2">
                      <p className="font-semibold text-slate-600 text-[10px]">{q ? q[displayLang] || q.de : qid}</p>
                      <p className="text-slate-800 mt-0.5">{answer}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Questions grouped by category */}
          {Object.entries(LANG_OPTIONS[0].id ? CAT_COLORS : {}).length === 0 && <p>Loading...</p>}
          {["personal", "health", "eating", "preferences", "lifestyle", "goals"].map(catId => {
            const catQuestions = grouped[catId];
            if (!catQuestions || catQuestions.length === 0) return null;
            const colors = CAT_COLORS[catId] || CAT_COLORS.goals;
            const catName = categories[catId]?.[displayLang] || categories[catId]?.de || catId;
            const collapsed = collapsedCats[catId];

            return (
              <div key={catId} className={`mb-4 rounded-xl border ${colors.border} overflow-hidden`} data-testid={`cat-${catId}`}>
                <div onClick={() => setCollapsedCats(prev => ({ ...prev, [catId]: !prev[catId] }))}
                  className={`flex items-center justify-between px-4 py-2.5 ${colors.header} text-white cursor-pointer`}>
                  <span className="text-xs font-bold">{catName} ({catQuestions.length})</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                </div>
                {!collapsed && (
                  <div className={`${colors.bg} divide-y ${colors.border}`}>
                    {catQuestions.map((q, idx) => (
                      <div key={q.id} className="flex items-start gap-2 px-4 py-2.5 group" data-testid={`question-${q.id}`}>
                        <div className="flex flex-col gap-0.5 pt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleMoveQuestion(q.id, "up")} className="p-0.5 rounded hover:bg-white/70"><ArrowUp className="w-3 h-3 text-slate-400" /></button>
                          <button onClick={() => handleMoveQuestion(q.id, "down")} className="p-0.5 rounded hover:bg-white/70"><ArrowDown className="w-3 h-3 text-slate-400" /></button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800">{q[displayLang] || q.de}</p>
                          {displayLang !== "de" && q.de && <p className="text-[10px] text-slate-400 mt-0.5">DE: {q.de}</p>}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${colors.text} ${colors.bg} border ${colors.border} font-medium`}>{TYPE_LABELS[q.type] || q.type}</span>
                            {q.custom && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Custom</span>}
                            {q.type === "choice" && q[`options_${displayLang}`] && (
                              <span className="text-[9px] text-slate-400 truncate max-w-[300px]">{q[`options_${displayLang}`]}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setEditingQ({ ...q })} className="p-1 rounded hover:bg-white/70 text-slate-400"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleDeleteQuestion(q.id)} className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Question Dialog */}
      <Dialog open={showAddQ} onOpenChange={setShowAddQ}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Neue Frage hinzufügen</DialogTitle>
            <DialogDescription>Frage in allen 3 Sprachen eingeben</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Kategorie</label>
                <select value={newQ.category} onChange={e => setNewQ(p => ({ ...p, category: e.target.value }))}
                  className="w-full text-xs border border-slate-200 rounded-md p-2" data-testid="new-q-category">
                  {Object.entries(categories).map(([id, names]) => (
                    <option key={id} value={id}>{names.de}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Typ</label>
                <select value={newQ.type} onChange={e => setNewQ(p => ({ ...p, type: e.target.value }))}
                  className="w-full text-xs border border-slate-200 rounded-md p-2" data-testid="new-q-type">
                  <option value="text">Kurzantwort</option>
                  <option value="textarea">Freitext</option>
                  <option value="choice">Auswahl</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-blue-600 uppercase mb-1 block">Frage (Deutsch) *</label>
              <Input value={newQ.de} onChange={e => setNewQ(p => ({ ...p, de: e.target.value }))} placeholder="Frage auf Deutsch..." data-testid="new-q-de" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Pergunta (Português)</label>
              <Input value={newQ.pt} onChange={e => setNewQ(p => ({ ...p, pt: e.target.value }))} placeholder="Pergunta em português..." data-testid="new-q-pt" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-violet-600 uppercase mb-1 block">Question (English)</label>
              <Input value={newQ.en} onChange={e => setNewQ(p => ({ ...p, en: e.target.value }))} placeholder="Question in English..." data-testid="new-q-en" />
            </div>
            {newQ.type === "choice" && (
              <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
                <p className="text-[10px] font-bold text-slate-500 uppercase">Optionen (kommagetrennt)</p>
                <Input value={newQ.options_de} onChange={e => setNewQ(p => ({ ...p, options_de: e.target.value }))} placeholder="Option1, Option2, Option3 (DE)" className="text-xs" />
                <Input value={newQ.options_pt} onChange={e => setNewQ(p => ({ ...p, options_pt: e.target.value }))} placeholder="Opção1, Opção2, Opção3 (PT)" className="text-xs" />
                <Input value={newQ.options_en} onChange={e => setNewQ(p => ({ ...p, options_en: e.target.value }))} placeholder="Option1, Option2, Option3 (EN)" className="text-xs" />
              </div>
            )}
            <Button onClick={handleAddQuestion} className="w-full" data-testid="submit-new-question">Frage hinzufügen</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={!!editingQ} onOpenChange={() => setEditingQ(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Frage bearbeiten</DialogTitle>
            <DialogDescription>Texte in allen Sprachen anpassen</DialogDescription>
          </DialogHeader>
          {editingQ && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Kategorie</label>
                  <select value={editingQ.category} onChange={e => setEditingQ(p => ({ ...p, category: e.target.value }))}
                    className="w-full text-xs border border-slate-200 rounded-md p-2">
                    {Object.entries(categories).map(([id, names]) => (
                      <option key={id} value={id}>{names.de}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Typ</label>
                  <select value={editingQ.type} onChange={e => setEditingQ(p => ({ ...p, type: e.target.value }))}
                    className="w-full text-xs border border-slate-200 rounded-md p-2">
                    <option value="text">Kurzantwort</option>
                    <option value="textarea">Freitext</option>
                    <option value="choice">Auswahl</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-blue-600 uppercase mb-1 block">Deutsch</label>
                <Input value={editingQ.de} onChange={e => setEditingQ(p => ({ ...p, de: e.target.value }))} />
              </div>
              <div>
                <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Português</label>
                <Input value={editingQ.pt} onChange={e => setEditingQ(p => ({ ...p, pt: e.target.value }))} />
              </div>
              <div>
                <label className="text-[10px] font-bold text-violet-600 uppercase mb-1 block">English</label>
                <Input value={editingQ.en} onChange={e => setEditingQ(p => ({ ...p, en: e.target.value }))} />
              </div>
              {editingQ.type === "choice" && (
                <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Optionen</p>
                  <Input value={editingQ.options_de || ""} onChange={e => setEditingQ(p => ({ ...p, options_de: e.target.value }))} placeholder="DE Optionen" className="text-xs" />
                  <Input value={editingQ.options_pt || ""} onChange={e => setEditingQ(p => ({ ...p, options_pt: e.target.value }))} placeholder="PT Opções" className="text-xs" />
                  <Input value={editingQ.options_en || ""} onChange={e => setEditingQ(p => ({ ...p, options_en: e.target.value }))} placeholder="EN Options" className="text-xs" />
                </div>
              )}
              <Button onClick={handleUpdateQuestion} className="w-full">Speichern</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Generate Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-500" /> KI Fragen generieren</DialogTitle>
            <DialogDescription>KI erstellt 8 zusätzliche Anamnese-Fragen in DE/PT/EN</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Kontext (optional)</label>
              <textarea value={aiContext} onChange={e => setAiContext(e.target.value)}
                className="w-full text-sm p-2 border border-slate-200 rounded-md resize-y min-h-[60px] focus:outline-none focus:border-emerald-400"
                placeholder="z.B. Fokus auf EMS-Training, Sportler, ältere Kunden..." rows={3} data-testid="ai-context" />
            </div>
            <Button onClick={handleAIGenerate} disabled={generating} className="w-full bg-gradient-to-r from-emerald-600 to-teal-600" data-testid="ai-generate-confirm">
              {generating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generiere...</> : <><Sparkles className="w-4 h-4 mr-2" /> 8 Fragen generieren</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
